import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    if (event.detail.data.clientOptimistic && event.detail.resource) {
      this.#updatePriceFromVariant(event.detail.resource);
      this.#updateInstallments(event.detail.resource.id);
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    this.#updateInstallments(event.detail.resource?.id);
  };

  /**
   * Updates the main PDP price block from local variant data for optimistic UI.
   * @param {any} variant
   */
  #updatePriceFromVariant(variant) {
    const { priceContainer } = this.refs;
    if (!priceContainer) return;

    const moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'USD';
    const compareAtPrice = Number(variant.compare_at_price || 0);
    const price = Number(variant.price || 0);
    const hasComparePrice = compareAtPrice > price;

    const regularRow = priceContainer.querySelector('.price__regular');
    const saleRow = priceContainer.querySelector('.price__sale');
    const regularCurrent = regularRow?.querySelector('.horizon-price__current, .price');
    const saleCurrent = saleRow?.querySelector('.horizon-price__current, .price-item--sale');
    const compareNode = saleRow?.querySelector('.horizon-price__compare, .compare-at-price');
    const badgeText = saleRow?.querySelector('.horizon-price__badge-text');

    const formattedPrice = formatMoney(price, moneyFormat, currency);

    if (hasComparePrice) {
      const formattedCompare = formatMoney(compareAtPrice, moneyFormat, currency);
      const formattedSaved = formatMoney(compareAtPrice - price, moneyFormat, currency);

      regularRow?.classList.add('price__hidden');
      saleRow?.classList.remove('price__hidden');
      if (saleCurrent) saleCurrent.innerHTML = formattedPrice;
      if (compareNode) compareNode.innerHTML = formattedCompare;
      if (badgeText) badgeText.innerHTML = `SAVE ${formattedSaved}`;
    } else {
      regularRow?.classList.remove('price__hidden');
      saleRow?.classList.add('price__hidden');
      if (regularCurrent) regularCurrent.innerHTML = formattedPrice;
    }
  }

  /**
   * Updates the installments input if it exists.
   * @param {string | number | null | undefined} variantId
   */
  #updateInstallments(variantId) {
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = variantId ? String(variantId) : '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
